import React, { useState, useEffect } from 'react';
import { COLORS } from '../config/constants';
import { dashboardService, externalService } from '../services/api.service';
import StatCard from '../components/StatCard';
import RecentActivitiesList from '../components/RecentActivitiesList';
import HomePage from './HomePage';
import GeneralTracking from './GeneralTracking';
import Projects from './Projects';
import StockList from './StockList';
import ProductionList from './ProductionList';
import ShipmentList from './ShipmentList';
import UserManagement from './UserManagement';
import Settings from './Settings';
import Reports from './Reports';
import Tracking from './Tracking';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const Dashboard = ({ user, onLogout }) => {
  const [stats, setStats] = useState({
    total_stock_weight: 0,
    active_projects: 0,
    daily_production: 0,
    pending_shipments: 0
  });
  const [activities, setActivities] = useState([]);
  const [productionChart, setProductionChart] = useState({ labels: [], datasets: [] });
  const [stockChart, setStockChart] = useState({ labels: [], datasets: [] });
  const [exchangeRates, setExchangeRates] = useState([]);
  const [lmePrices, setLmePrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('dashboard');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsData, activitiesData, productionData, stockData, ratesData, pricesData] = await Promise.all([
        dashboardService.getStats(),
        dashboardService.getRecentActivities(10),
        dashboardService.getProductionChart(),
        dashboardService.getStockChart(),
        externalService.getExchangeRates(),
        externalService.getLMEPrices()
      ]);

      setStats(statsData);
      setActivities(activitiesData);
      setProductionChart(productionData);
      setStockChart(stockData);
      setExchangeRates(ratesData);
      setLmePrices(pricesData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Transform chart data for recharts
  const productionChartData = productionChart.labels?.map((label, index) => ({
    name: label,
    value: productionChart.datasets?.[0]?.data?.[index] || 0
  })) || [];

  const stockChartData = stockChart.labels?.map((label, index) => ({
    name: label,
    value: stockChart.datasets?.[0]?.data?.[index] || 0
  })) || [];

  const CHART_COLORS = ['#ae0404', '#333333', '#666666', '#999999', '#cccccc'];

  return (
    <div data-testid="dashboard-page" style={{ display: 'flex', minHeight: '100vh', background: COLORS.background }}>
      {/* Sidebar */}
      <div
        style={{
          width: '260px',
          background: COLORS.secondary,
          color: COLORS.white,
          display: 'flex',
          flexDirection: 'column',
          position: 'fixed',
          height: '100vh',
          left: 0,
          top: 0,
          overflowY: 'auto'
        }}
      >
        {/* Logo */}
        <div style={{ padding: '2rem 1.5rem', borderBottom: `1px solid rgba(255,255,255,0.1)` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {/* Abell Logo */}
            <img 
              src="/abell-logo.svg" 
              alt="Abell Logo" 
              style={{
                width: '50px',
                height: 'auto',
                flexShrink: 0
              }}
            />
            <div>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', letterSpacing: '1px' }}>ABELL</h2>
              <p style={{ margin: 0, fontSize: '0.75rem', opacity: 0.8 }}>Takip Sistemi</p>
            </div>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav style={{ padding: '1rem 0', flex: 1 }}>
          {[
            { id: 'dashboard', label: 'Dashboard', icon: '📊' },
            { id: 'anasayfa', label: 'ANASAYFA', icon: '🏠' },
            { id: 'genel-takip', label: 'Genel Takip', icon: '📍' },
            { id: 'projects', label: 'Projeler', icon: '📁' },
            { id: 'stock', label: 'Stok Kayıtları', icon: '📦' },
            { id: 'production', label: 'Üretim Liste', icon: '🛠️' },
            { id: 'shipment', label: 'Sevk Liste', icon: '🚚' },
            { id: 'tracking', label: 'Takip', icon: '📍' },
            { id: 'reports', label: 'Raporlar', icon: '📈' },
            ...(user?.role === 'admin' || user?.role === 'operator' ? [{ id: 'settings', label: 'Ayarlar', icon: '⚙️' }] : []),
            ...(user?.role === 'admin' ? [{ id: 'users', label: 'Kullanıcılar', icon: '👥' }] : [])
          ].map((item) => (
            <button
              key={item.id}
              data-testid={`menu-${item.id}`}
              onClick={() => setActiveSection(item.id)}
              style={{
                width: '100%',
                padding: '0.875rem 1.5rem',
                background: activeSection === item.id ? COLORS.primary : 'transparent',
                color: COLORS.white,
                border: 'none',
                textAlign: 'left',
                fontSize: '0.95rem',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                fontWeight: activeSection === item.id ? '600' : '400'
              }}
              onMouseEnter={(e) => {
                if (activeSection !== item.id) {
                  e.target.style.background = 'rgba(255,255,255,0.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (activeSection !== item.id) {
                  e.target.style.background = 'transparent';
                }
              }}
            >
              <span style={{ fontSize: '1.25rem' }}>{item.icon}</span>
              {item.label}
            </button>
          ))}
        </nav>

        {/* User Info & Logout */}
        <div style={{ padding: '1.5rem', borderTop: `1px solid rgba(255,255,255,0.1)` }}>
          <div style={{ marginBottom: '1rem' }}>
            <p style={{ fontSize: '0.85rem', opacity: 0.7, margin: 0 }}>Kullanıcı</p>
            <p style={{ fontSize: '0.95rem', fontWeight: '600', margin: '0.25rem 0 0 0' }}>
              {user?.username || 'User'}
            </p>
            <p style={{ fontSize: '0.75rem', opacity: 0.6, margin: '0.25rem 0 0 0', textTransform: 'capitalize' }}>
              {user?.role || 'viewer'}
            </p>
          </div>
          <button
            data-testid="logout-button"
            onClick={onLogout}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: 'rgba(255,255,255,0.1)',
              color: COLORS.white,
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: '500',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.15)'}
            onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.1)'}
          >
            Çıkış Yap
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ marginLeft: '260px', flex: 1, padding: '2rem' }}>
        {activeSection === 'anasayfa' ? (
          <HomePage user={user} />
        ) : activeSection === 'genel-takip' ? (
          <GeneralTracking user={user} />
        ) : activeSection === 'projects' ? (
          <Projects user={user} />
        ) : activeSection === 'stock' ? (
          <StockList user={user} />
        ) : activeSection === 'production' ? (
          <ProductionList user={user} />
        ) : activeSection === 'shipment' ? (
          <ShipmentList user={user} />
        ) : activeSection === 'tracking' ? (
          <Tracking user={user} />
        ) : activeSection === 'reports' ? (
          <Reports user={user} />
        ) : activeSection === 'users' && user?.role === 'admin' ? (
          <UserManagement user={user} />
        ) : activeSection === 'settings' ? (
          <Settings user={user} />
        ) : activeSection === 'dashboard' ? (
          loading ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <div style={{ fontSize: '2rem', color: COLORS.primary, marginBottom: '1rem' }}>⏳</div>
              <p style={{ color: COLORS.textLight }}>Yükleniyor...</p>
            </div>
          ) : (
          <>
            {/* Header */}
            <div style={{ marginBottom: '2rem' }}>
              <h1 style={{ fontSize: '2rem', fontWeight: '700', color: COLORS.text, margin: 0 }}>
                Dashboard
              </h1>
              <p style={{ color: COLORS.textLight, marginTop: '0.5rem' }}>
                Hoş geldiniz, {user?.full_name || user?.username}
              </p>
            </div>

            {/* Stats Cards */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '1.5rem',
                marginBottom: '2rem'
              }}
            >
              <StatCard
                title="Toplam Stok"
                value={`${stats.total_stock_weight.toLocaleString('tr-TR')} kg`}
                subtitle="Güncel stok ağırlığı"
                icon="📦"
                color={COLORS.primary}
              />
              <StatCard
                title="Aktif Projeler"
                value={stats.active_projects}
                subtitle={`${stats.total_projects} toplam proje`}
                icon="📁"
                color="#f59e0b"
              />
              <StatCard
                title="Günlük Üretim"
                value={`${stats.daily_production.toLocaleString('tr-TR')} kg`}
                subtitle="Bugünkü üretim"
                icon="🛠️"
                color="#10b981"
              />
              <StatCard
                title="Bekleyen Sevkiyat"
                value={stats.pending_shipments}
                subtitle="İşlem bekliyor"
                icon="🚚"
                color="#6366f1"
              />
            </div>

            {/* Charts Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2rem' }}>
              {/* Production Chart */}
              <div
                style={{
                  background: COLORS.white,
                  borderRadius: '12px',
                  padding: '1.5rem',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
              >
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: COLORS.text, marginBottom: '1.5rem' }}>
                  Son 7 Gün Üretim
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={productionChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" stroke={COLORS.textLight} style={{ fontSize: '0.85rem' }} />
                    <YAxis stroke={COLORS.textLight} style={{ fontSize: '0.85rem' }} />
                    <Tooltip />
                    <Bar dataKey="value" fill={COLORS.primary} radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Stock Chart */}
              <div
                style={{
                  background: COLORS.white,
                  borderRadius: '12px',
                  padding: '1.5rem',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
              >
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: COLORS.text, marginBottom: '1.5rem' }}>
                  Stok Dağılımı
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={stockChartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label
                    >
                      {stockChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Bottom Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem' }}>
              {/* Recent Activities */}
              <div
                style={{
                  background: COLORS.white,
                  borderRadius: '12px',
                  padding: '1.5rem',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
              >
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: COLORS.text, marginBottom: '1.5rem' }}>
                  Son Aktiviteler
                </h3>
                <RecentActivitiesList activities={activities} />
              </div>

              {/* External Data */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {/* Exchange Rates */}
                <div
                  style={{
                    background: COLORS.white,
                    borderRadius: '12px',
                    padding: '1.5rem',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                  }}
                >
                  <h3 style={{ fontSize: '1rem', fontWeight: '600', color: COLORS.text, marginBottom: '1rem' }}>
                    💱 Döviz Kurları
                  </h3>
                  {exchangeRates.map((rate) => (
                    <div
                      key={rate.currency}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '0.5rem 0',
                        borderBottom: `1px solid ${COLORS.background}`
                      }}
                    >
                      <span style={{ fontWeight: '600', color: COLORS.text }}>{rate.currency}</span>
                      <span style={{ color: COLORS.textLight }}>
                        {rate.rate.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  ))}
                </div>

                {/* LME Prices */}
                <div
                  style={{
                    background: COLORS.white,
                    borderRadius: '12px',
                    padding: '1.5rem',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                  }}
                >
                  <h3 style={{ fontSize: '1rem', fontWeight: '600', color: COLORS.text, marginBottom: '1rem' }}>
                    🔩 LME Fiyatları
                  </h3>
                  {lmePrices.map((price) => (
                    <div
                      key={price.metal}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '0.5rem 0',
                        borderBottom: `1px solid ${COLORS.background}`
                      }}
                    >
                      <span style={{ fontWeight: '600', color: COLORS.text, fontSize: '0.9rem' }}>{price.metal}</span>
                      <span style={{ color: COLORS.textLight, fontSize: '0.85rem' }}>
                        ${price.price.toLocaleString('en-US')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )) : (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <p style={{ color: COLORS.textLight, fontSize: '1.1rem' }}>
              Bu bölüm yakında aktif olacak
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
